<template>
  <RevDrawer
    :name="MODAL_NAME"
    size="large"
    :title="i18n(translations.title, { market: i18n.country(market) })"
    @open="handleDrawerOpen"
  >
    <template #body>
      <RevLoadingScreen
        v-if="isSubmitting"
        class="mt-56"
        :text="i18n(translations.loadingText)"
      />
      <div v-if="!isSubmitting">
        <div class="relative flex-1 space-y-24 md:space-y-56">
          <RevForm
            :id="FORM_ID"
            :key="eprDetails.length"
            :data-test="FORM_ID"
            :initial-values="INITIAL_VALUES"
            :name="FORM_ID"
            :validate
            @submit="handleAddEprDetail"
          >
            <template #default="{ values, errors, hasError }">
              <RevInfoBlock
                v-if="shouldShowInfoBlock"
                class="mb-20"
                :icon="IconAlert"
                :title="i18n(translations.missingDeCategories)"
                variant="warning"
              >
                <ul class="ml-12 mt-4">
                  <li v-if="isPackagingMissing" class="body-2">
                    {{ getMissingLabel('packagingNumber') }}
                  </li>
                  <li v-if="isEeeMissing" class="body-2">
                    {{ getMissingLabel('eeeNumber') }}
                  </li>
                  <li v-if="isBatteriesMissing" class="body-2">
                    {{ getMissingLabel('batteriesNumber') }}
                  </li>
                </ul>
              </RevInfoBlock>
              <div>
                <div class="mb-12">
                  {{ i18n(translations.categoryLabel) }}
                </div>
                <div class="md:flex">
                  <RevInputSelect
                    id="eprCategorySelect"
                    v-model="values.category"
                    class="mt-8 w-full md:mr-8 md:w-1/4"
                    :error="errors.category"
                    :label="i18n(translations.category)"
                    :options="eprCategoryOptions"
                  />
                  <div class="mt-8 w-full md:mr-8 md:w-1/4">
                    <RevInputText
                      id="eprNumberInput"
                      v-model="values.eprNumber"
                      :disabled="shouldDisableEprNumberInput(values)"
                      :error="errors.eprNumber"
                      :label="i18n(translations.eprId)"
                    />
                    <p
                      class="text-action-default-low body-2 mt-8 flex items-baseline peer-disabled:text-action-default-hi-disabled"
                    >
                      {{ eprNumberInputHint }}
                    </p>
                  </div>
                  <div
                    v-if="shouldHaveDocument(values.category)"
                    class="mt-8 w-full md:mr-8 md:w-1/4"
                  >
                    <div v-if="shouldShowDocumentUploadInput(values)">
                      <RevInputFileUpload
                        id="epr_document_upload_input"
                        v-model="values.files"
                        :accept="ALLOWED_FILES_EXTENSIONS"
                        :error="errors.files"
                        :label-empty="i18n(translations.uploadCertificate)"
                        :label-not-empty="i18n(translations.uploadCertificate)"
                        :max-file-size="ALLOWED_FILES_SIZE"
                        :max-files="ALLOWED_FILES_COUNT"
                      />
                      <p
                        v-if="errors.files"
                        class="text-static-danger-hi body-2 mt-8 flex items-baseline peer-disabled:text-static-danger-hi-disabled"
                      >
                        {{ errors.files }}
                      </p>
                    </div>
                    <div
                      v-if="shouldShowUploadedDocument(values)"
                      class="mt-4 flex w-full items-center align-middle"
                    >
                      <div class="flex-1 truncate">
                        {{ values.files[0].name }}
                      </div>
                      <RevButtonIcon
                        :icon="IconTrash"
                        variant="secondary"
                        @click="handleDeleteUploadedDocument"
                      />
                    </div>
                  </div>
                  <RevCheckbox
                    v-if="canEprDetailBeExempted(values)"
                    id="eprExemptionCheckbox"
                    v-model="values.isExempted"
                    :checked="values.isExempted"
                    class="mr-16 mt-20 block md:ml-8"
                  >
                    <template #label>
                      {{ getExemptFromLabel(values) }}
                    </template>
                  </RevCheckbox>
                  <div class="mt-16 flex md:my-auto md:mt-8 md:w-auto">
                    <RevButton
                      class="grow md:grow-0"
                      :disabled="hasError"
                      type="submit"
                      variant="primary"
                    >
                      {{ i18n(translations.add) }}
                    </RevButton>
                  </div>
                </div>
              </div>
            </template>
          </RevForm>
          <div v-if="eprDetails.length">
            <div class="mb-16">
              {{ i18n(translations.eprNumbersToAddText) }}
            </div>
            <RevTable :collection="eprDetails" :columns transparent-header>
              <template #body-category="{ item }">
                <span class="body-2-bold">
                  {{ getCategoryLabel(item.category as EprCategoryCode) }}
                </span>
              </template>
              <template #body-eprNumber="{ item }">
                {{
                  item.isExempted ? i18n(translations.exempt) : item.eprNumber
                }}
              </template>
              <template #[`body-${TABLE_COLUMN_KEYS.DOWNLOAD}`]="{ item }">
                <div
                  v-if="item.files.length"
                  class="border-static-default-mid rounded-md flex max-w-[450px] items-center border p-4"
                >
                  <IconBill class="flex shrink-0" size="medium" />
                  <div class="ml-8 flex shrink truncate">
                    <span class="truncate">{{ item.files[0].name }}</span>
                  </div>
                </div>
              </template>
              <template #[`body-${TABLE_COLUMN_KEYS.ACTION}`]="{ item }">
                <RevButtonIcon
                  :data-test="`${item.category}_${item.eprNumber}`"
                  :icon="IconTrash"
                  variant="secondary"
                  @click="handleDeleteEprDetail(item)"
                />
              </template>
            </RevTable>
          </div>
          <div class="flex w-full grow">
            <RevButton
              id="save_epr_details_button"
              class="mt-8 grow md:mt-32 md:grow-0"
              :disabled="!eprDetails.length"
              :loading="isSubmitting"
              variant="primary"
              @click="handleSubmit"
            >
              {{ i18n(translations.addDetails) }}
            </RevButton>
          </div>
        </div>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type {
  EprCategoryCode,
  GetLegalDetailsMetadataMarketRequirements,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevDrawer } from '@ds/components/Drawer'
import {
  FORM_VALID,
  RevForm,
  type Validator,
  makeValidate,
  required,
  setFormValues,
} from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { closeModal } from '@ds/components/ModalBase'
import { RevTable } from '@ds/components/Table'
import { IconAlert } from '@ds/icons/IconAlert'
import { IconBill } from '@ds/icons/IconBill'
import { IconTrash } from '@ds/icons/IconTrash'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { FORM_IDS, MODAL_NAMES } from '../../../../../../Profile.constants'
import { uploadLegalDocument } from '../../../../../../Profile.utils'
import {
  ALLOWED_FILES_COUNT,
  ALLOWED_FILES_EXTENSIONS,
  ALLOWED_FILES_SIZE,
} from '../../../../LegalDetails.constants'
import { EPR_CATEGORIES } from '../../EprDetails.constants'

import translations from './EprDetailsModal.translations'
import type {
  FormEprValues,
  FormattedFormEprValues,
} from './EprDetailsModal.types'

const MODAL_NAME = MODAL_NAMES.EDIT_EPR_DETAILS
const FORM_ID = FORM_IDS.EDIT_EPR_DETAILS

const TABLE_COLUMN_KEYS = {
  DOWNLOAD: 'download',
  ACTION: 'action',
}

const props = defineProps<{
  market: `${MarketCountryCode}`
  marketRequirements: GetLegalDetailsMetadataMarketRequirements
}>()

const emit = defineEmits(['update-epr-information'])

const i18n = useI18n()
const { id: sellerId } = useSellerInfoStore()
const { openSuccessToast, openErrorToast } = useTheToast()

const cancelUploadFilePoll = ref<() => void>()

onUnmounted(() => {
  if (cancelUploadFilePoll.value) {
    cancelUploadFilePoll.value()
  }
})

const isSubmitting = ref(false)
const eprDetails = ref<FormattedFormEprValues[]>([])

const INITIAL_VALUES: FormEprValues = {
  category: '',
  eprNumber: '',
  files: [],
  isExempted: false,
}

const eprCategoryOptions = computed(() =>
  Object.entries(props.marketRequirements).map(([value, { label }]) => ({
    label,
    value,
  })),
)

const eprNumberInputHint = computed(() =>
  i18n(
    translations[`eprNumberHint${props.market}` as keyof typeof translations],
  ),
)

const isEeeMissing = computed(
  () =>
    !eprDetails.value.some(
      (eprDetail) => eprDetail.category === EPR_CATEGORIES.DEEE,
    ),
)
const isBatteriesMissing = computed(
  () =>
    !eprDetails.value.some(
      (eprDetail) => eprDetail.category === EPR_CATEGORIES.BATTERIES,
    ),
)
const isPackagingMissing = computed(
  () =>
    !eprDetails.value.some(
      (eprDetail) => eprDetail.category === EPR_CATEGORIES.PACKAGING,
    ),
)

const shouldShowInfoBlock = computed(
  () =>
    (isPackagingMissing.value ||
      isEeeMissing.value ||
      isBatteriesMissing.value) &&
    props.market === MarketCountryCode.DE,
)

const columns = [
  { key: 'category', label: i18n(translations.category) },
  { key: 'eprNumber', label: i18n(translations.eprNumber) },
  { key: TABLE_COLUMN_KEYS.DOWNLOAD, label: '' },
  { key: TABLE_COLUMN_KEYS.ACTION, label: '', align: 'end' as const },
]

function getMissingLabel(prefix: string) {
  return i18n(
    translations[`${prefix}${props.market}` as keyof typeof translations],
  )
}

function getCategory(categoryCode: EprCategoryCode) {
  return props.marketRequirements[categoryCode]
}

function shouldDisableEprNumberInput({ isExempted, category }: FormEprValues) {
  return isExempted && !!category && getCategory(category).canBeExempted
}

function shouldHaveDocument(category: FormEprValues['category']) {
  return !!category && props.marketRequirements[category].documentType
}

function shouldShowDocumentUploadInput({ category, files }: FormEprValues) {
  return !files.length && shouldHaveDocument(category)
}

function shouldShowUploadedDocument({ category, files }: FormEprValues) {
  return files.length && shouldHaveDocument(category)
}

function canEprDetailBeExempted({ category }: FormEprValues) {
  return !!category && getCategory(category).canBeExempted
}

function getExemptFromLabel({ category }: FormEprValues) {
  return (
    category &&
    i18n(translations.exemptFrom, { category: getCategory(category).label })
  )
}

function getCategoryLabel(category: EprCategoryCode) {
  return category === EPR_CATEGORIES.DEEE
    ? i18n(translations.fullDeeeLabel)
    : getCategory(category).label
}

const validate = computed(() =>
  makeValidate<FormEprValues>({
    category: [required(i18n(translations.fieldIsRequired))],
    eprNumber: [
      ((
        eprNumber: FormEprValues['eprNumber'],
        { category, isExempted }: FormEprValues,
      ) => {
        if (isExempted || !category) {
          return FORM_VALID
        }
        if (!eprNumber.trim()) {
          return i18n(translations.fieldIsRequired)
        }
        if (
          eprDetails.value.some(
            (eprDetail) =>
              eprDetail.eprNumber === eprNumber &&
              eprDetail.category === category,
          )
        ) {
          return i18n(translations.eprNumberNotUnique)
        }

        return new RegExp(
          props.marketRequirements[category].pattern || '',
        ).test(eprNumber)
          ? FORM_VALID
          : i18n(translations.fieldIsRequired) /* TODO missing translation */
      }) as Validator<FormEprValues>,
    ],
    isExempted: [
      ((isExempted: FormEprValues['isExempted'], values: FormEprValues) => {
        return isExempted && !canEprDetailBeExempted(values)
          ? i18n(translations.fieldIsRequired)
          : FORM_VALID
      }) as Validator<FormEprValues>,
    ],
  }),
)

function formatEprDetail(eprDetail: FormEprValues): FormattedFormEprValues {
  return {
    ...eprDetail,
    eprNumber: eprDetail.isExempted ? null : eprDetail.eprNumber,
    files: shouldHaveDocument(eprDetail.category) ? eprDetail.files : [],
  }
}

function handleAddEprDetail(values: FormEprValues) {
  eprDetails.value = [...eprDetails.value, formatEprDetail(values)]
}

function handleDeleteEprDetail(eprDetailToDelete: FormattedFormEprValues) {
  eprDetails.value = eprDetails.value.filter(
    (eprDetail) =>
      !(
        eprDetail.category === eprDetailToDelete.category &&
        eprDetail.eprNumber === eprDetailToDelete.eprNumber
      ),
  )
}

function handleDeleteUploadedDocument() {
  setFormValues(FORM_ID, { files: [] })
}

function handleDrawerOpen() {
  eprDetails.value = []
}

async function prepareEprDetailToPost(eprDetail: FormattedFormEprValues) {
  let file = null
  if (sellerId && eprDetail.files.length) {
    file = await uploadLegalDocument(
      sellerId,
      eprDetail.files[0],
      getCategory(eprDetail.category as EprCategoryCode).documentType,
      cancelUploadFilePoll,
    )
  }

  return {
    ...eprDetail,
    files: undefined,
    market: props.market,
    documentId: file?.id || null,
  }
}

async function handleSubmit() {
  isSubmitting.value = true
  try {
    // Upload files and update EPR information
    await Promise.all(
      eprDetails.value.map(async (eprDetail) =>
        $httpFetch(sellerProfileAPI.postEprInformation, {
          body: await prepareEprDetailToPost(eprDetail),
        }),
      ),
    )
    openSuccessToast({
      title: i18n(translations.eprInformationUpdateSuccessTitle),
      content: i18n(translations.eprInformationUpdateSuccessMessage),
    })
    emit('update-epr-information')
    closeModal(MODAL_NAME)
  } catch {
    openErrorToast({
      title: i18n(translations.eprInformationPostErrorTitle),
      content: i18n(translations.eprInformationPostErrorMessage),
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>
